import type { Root } from 'mdast';
import type { Plugin } from 'unified';
import { visit } from 'unist-util-visit';

/**
 * Collect the http(s) link targets of a document into `sink` — unique, in
 * source order — so `MarkdownContent` can render `trailing` content scanners
 * (link previews) after the prose.
 *
 * Reading them off the mdast is what makes the trailing stack well-defined:
 * order is the order the links appear in the text and dedupe is the URL
 * itself, neither of which depends on when a preview's data resolves or on
 * the order React happens to mount things. It also means only real links
 * count — a URL inside a code fence is a `code` node, never a `link`, so it is
 * not previewed.
 *
 * Only `http(s)` targets are collected. That excludes the internal
 * `reflex-scan:` links the text-scanner plugin synthesizes, and it is the same
 * scheme guard the `a` slot applies, so nothing a scanner would refuse to
 * navigate to can reach a trailing component.
 *
 * The sink is mutated in place (cleared on every pass) rather than returned,
 * because a remark plugin's only channel back to the caller is the closure it
 * was created with. `MarkdownContent` owns the array and reads it after the
 * render that filled it.
 */
export function remarkCollectLinkTargets(sink: string[]): Plugin<[], Root> {
  return function remarkCollectLinkTargets() {
    return (tree) => {
      sink.length = 0;
      const seen = new Set<string>();
      visit(tree, 'link', (node) => {
        if (!/^https?:/i.test(node.url) || seen.has(node.url)) return;
        seen.add(node.url);
        sink.push(node.url);
      });
    };
  };
}

/** Whether two collected-link lists are the same, in the same order. */
export function sameLinkTargets(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((href, i) => href === b[i]);
}
